import { createPortal } from 'react-dom';
import getLocal from '@/app/utils/getLocal';
import LoginFormModal from '@/components/LoginFormModal';
import { getLang } from 'lowcomp/plugins/member/utils';
import lang from '../lang';
import NoteModal from '../ModalComp/NoteModal';
import { useLoginGuard, useRewardsData, useRedeemLogic } from './hooks';
import { RewardsSection, RedeemSuccessModal, RedeemConfirmModal, PointsDisplay } from './components';
import PointsFilter from './PointsFilter';
import { RuleProps, TranslationFn } from './types';
import { DEFAULT_ACTIVE_TAB } from './constants';
import styled from './index.module.scss';

const EcoCreditRules = ({
    title,
    subTitle,
    pathUrl,
    setShowPage,
    refershData,
    modal,
    defauleActiveTab = DEFAULT_ACTIVE_TAB,
    customColor,
    isEditStatus = false,
    id = '',
    customStyle,
    loginType = 'page',
}: Partial<RuleProps>) => {
    const { locale } = getLocal();
    const t = getLang(lang, locale) as TranslationFn;

    // Login guard hook
    const { isLogin, showLoginModal, setShowLoginModal, isClient, guardLogin } = useLoginGuard({
        locale,
        title,
        id,
        loginType,
        t,
    });

    // Rewards data hook
    const { state, ecoState, discountList, productList, giftCardList, allData, filterTabs, activeRange, setActiveRange, refreshAllRules } = useRewardsData({
        locale,
        pathUrl,
        isEditStatus,
        defauleActiveTab,
        t,
    });

    // Redeem logic hook
    const {
        reedingLoading,
        currRules,
        setCurrRules,
        couponModalOpen,
        setCouponModalOpen,
        currCoupon,
        setCurrCoupon,
        currNotes,
        setCurrNotes,
        buyFormRef,
        openConfirmModal,
        onConfirm,
        shopNow,
    } = useRedeemLogic({
        locale,
        pathUrl,
        t,
        guardLogin,
        refreshAllRules,
        refershData,
    });

    const handleViewMyRewards = () => {
        setShowPage?.('coupon_modal$1');
        setCurrCoupon(null);
    };

    if (!filterTabs?.length) {
        if (isEditStatus) {
            return (
                <div className={`${styled.ecocredit_rules} ${modal ? 'modal' : ''}`}>
                    <div className="temp-member-product-scene-h-title credits-title">{title || t('redeem_ecocredits_title')}</div>
                    {subTitle && <div className="temp-member-product-scene-h-subTitle">{subTitle}</div>}
                </div>
            );
        }
        return <div></div>;
    }

    return (
        <div className={`${styled.ecocredit_rules} ${modal ? 'modal' : ''}`}>
            <div className="rule-wrapper">
                {/* Global styles for antd components */}
                <style global jsx>
                    {`
                        .ant-message {
                            z-index: 999999;
                            .ant-message-notice .ant-message-notice-content {
                                border-radius: 6px;
                                overflow: hidden;
                            }
                        }
                        .ant-modal-root {
                            z-index: 9999;
                        }
                    `}
                </style>

                {/* Title */}
                <div className="temp-member-product-scene-h-title credits-title">{title || t('redeem_ecocredits_title')}</div>

                {/* Subtitle */}
                {subTitle && <div className="temp-member-product-scene-h-subTitle credits-subtitle">{subTitle}</div>}

                {/* Filter and Points Container */}
                <div className={`container-center ${!isLogin ? 'container-tab-center' : ''}`}>
                    <PointsFilter activeRange={activeRange} onChange={setActiveRange} allData={allData} />
                    <PointsDisplay isLogin={isLogin} points={ecoState.rewardUserInfo?.points} locale={locale} t={t} />
                </div>

                {/* Discount Section */}
                <RewardsSection
                    title={t('discount')}
                    list={discountList}
                    swiperKey="discount"
                    state={state}
                    isLogin={isLogin}
                    ecoState={ecoState}
                    setCurrNotes={setCurrNotes}
                    openConfirmModal={openConfirmModal}
                    customColor={customColor}
                    hideAmount={false}
                />

                {/* Product Section */}
                <RewardsSection
                    title={t('product_text')}
                    list={productList}
                    swiperKey="product"
                    state={state}
                    isLogin={isLogin}
                    ecoState={ecoState}
                    setCurrNotes={setCurrNotes}
                    openConfirmModal={openConfirmModal}
                    customColor={customColor}
                    hideAmount={true}
                />

                {/* Gift Card Section - THIRD_COUPONS type should hide amount */}
                <RewardsSection
                    title={t('gift_card')}
                    list={giftCardList}
                    swiperKey="giftcard"
                    state={state}
                    isLogin={isLogin}
                    ecoState={ecoState}
                    setCurrNotes={setCurrNotes}
                    openConfirmModal={openConfirmModal}
                    customColor={customColor}
                    hideAmount={true}
                />

                {/* Success Modal */}
                <RedeemSuccessModal
                    open={couponModalOpen}
                    coupon={currCoupon}
                    locale={locale}
                    t={t}
                    onClose={() => setCouponModalOpen(false)}
                    onViewMyRewards={handleViewMyRewards}
                    onShopNow={shopNow}
                    setCurrNotes={setCurrNotes}
                />

                {/* Confirm Modal */}
                <RedeemConfirmModal
                    rule={currRules}
                    loading={reedingLoading}
                    t={t}
                    onConfirm={onConfirm}
                    onClose={() => setCurrRules(null)}
                    buyFormRef={buyFormRef}
                    customColor={customColor}
                    customStyle={customStyle}
                />

                {/* Notes Modal */}
                <NoteModal currNotes={currNotes} setCurrNotes={setCurrNotes} customColor={customColor} />

                {/* Login Modal */}
                {isClient &&
                    showLoginModal &&
                    createPortal(
                        <LoginFormModal
                            visible={showLoginModal}
                            onCancel={() => {
                                setShowLoginModal(false);
                            }}
                            type="passwordless"
                        />,
                        document.body,
                    )}
            </div>
        </div>
    );
};

export default EcoCreditRules;
